import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, RefreshCw, ScrollText } from 'lucide-react'
import { getProjectHandoverLog, getEAProject } from '../services/ea-api'
import EAHandoverEntry from '../components/ea/EAHandoverEntry'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

type LogLevelFilter = 'all' | 'info' | 'warning' | 'error'

const FILTER_OPTIONS: { value: LogLevelFilter; label: string }[] = [
  { value: 'all', label: 'All Levels' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
]

export default function EAHandoverLog() {
  const { projectId } = useParams<{ projectId: string }>()
  const [levelFilter, setLevelFilter] = useState<LogLevelFilter>('all')

  const { data: project } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const {
    data: logData,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['ea-handover-log', projectId],
    queryFn: () => getProjectHandoverLog(projectId!, { limit: 200 }).then((res) => res.data),
    enabled: !!projectId,
    refetchInterval: 10000,
  })

  const entries = Array.isArray(logData) ? logData : []

  const filteredEntries = entries
    .filter((e: any) => {
      if (levelFilter === 'all') return true
      return (e.log_level || '').toLowerCase() === levelFilter
    })
    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <RefreshCw size={24} className="animate-spin" style={{ color: '#60a5fa' }} />
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading handover log...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link
            to={`/ea/projects/${projectId}`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
            }}
            aria-label="Back to project"
          >
            <ArrowLeft size={18} style={{ color: '#60a5fa' }} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
              Handover Log
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {project?.project_name || 'Project'} — Agent handover timeline
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            Auto-refreshing every 10s
            {dataUpdatedAt ? ` — last: ${new Date(dataUpdatedAt).toLocaleTimeString()}` : ''}
          </span>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: '#60a5fa',
            }}
            aria-label="Refresh log"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Filter:
            </span>
            {FILTER_OPTIONS.map((opt) => {
              const isActive = levelFilter === opt.value
              return (
                <button
                  key={opt.value}
                  onClick={() => setLevelFilter(opt.value)}
                  className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
                  style={{
                    background: isActive ? 'rgba(59, 130, 246, 0.25)' : 'rgba(30, 41, 59, 0.5)',
                    border: `1px solid ${isActive ? 'rgba(59, 130, 246, 0.5)' : 'rgba(100, 180, 255, 0.15)'}`,
                    color: isActive ? '#60a5fa' : 'rgba(148, 163, 184, 0.7)',
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {filteredEntries.length} entr{filteredEntries.length === 1 ? 'y' : 'ies'}
          </span>
        </div>
      </div>

      {/* Timeline */}
      {filteredEntries.length > 0 ? (
        <div className="pl-2">
          {filteredEntries.map((entry: any, idx: number) => (
            <EAHandoverEntry
              key={entry.id || idx}
              entry={entry}
              isLast={idx === filteredEntries.length - 1}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
          >
            <ScrollText size={28} style={{ color: 'rgba(96, 165, 250, 0.5)' }} />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            No handover log entries
          </h3>
          <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {levelFilter !== 'all'
              ? 'No entries match this log level filter. Try selecting "All Levels".'
              : 'Agent handover events will appear here once pipeline tasks begin running.'}
          </p>
        </div>
      )}
    </div>
  )
}
